import { MaterialIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { Image, Linking, Platform, StyleSheet, TouchableOpacity, View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { MapView } from '../map/MapView';

import { isPlentifulProvider } from '../../Location';
import Colors from '../../constants/Colors';
import { EnvVars } from '../../constants/EnvConfig';
import Layout from '../../constants/Layout';
import { ServiceType } from '../../types';
import { getAdditionalRequestedFields } from '../../utils/additionalInfo';
import { formatPhoneNumber } from '../../utils/formatPhone';
import { logAnalyticsEvent } from '../../utils/logAnalyticsEvent';
import showLocation from '../../utils/showLocation';
import MaterialSwitch from '../../utils/switch';
import timeUtils from '../../utils/timeUtils';
import FilledButton from '../FilledButton';
import TefapAttestation from '../profile/TefapAttestation/TefapAttestation';
import TefapStatus from '../profile/TefapStatus';
import PantryDetailsProgramPrompt from '../state-programs/PantryDetailsProgramPrompt.tsx';
import { StateProgramContextProvider } from '../state-programs/StateProgramContext.tsx';
import StateProgramFacade from '../state-programs/StateProgramFacade';
import StateProgramsErrorBoundary from '../state-programs/StateProgramsErrorBoundary.tsx';
import PushNotificationRegistration from '../profile/PushNotificationRegistration';
import AdditionalInfoRequired from './AdditionalInfoRequired';
import ConfirmCancel from './ConfirmCancel';
import ExpandableServiceSchedule from './ExpandableServiceSchedule.tsx';
import MakeAppointment from './MakeAppointment';
import MissingInfoModal from './MissingInfoModal';
import MustLogin from './MustLogin';
import MustVerify from './MustVerify';
import PantryDistance from './PantryDistance.tsx';
import PantrySpinner from './PantrySpinner';
import ReservationConfirmedModal from './ReservationConfirmedModal.tsx';
import SelectEditCancelVisit from './SelectEditCancelVisit';
import { UserVisitSummaryWithIcon } from './UserVisitSummaryWithIcon.tsx';
import { getMapMarkerIcon } from './pantryIcon';
import { logError } from '../../utils/logger';
import PantryDetailsMarker from './PantryDetailsMarker';
// eslint-disable-next-line import/extensions, import/no-unresolved
import { showAlert } from '../../utils/alert'; // platform-specific: alert.native.ts / alert.web.ts

import { AppText } from '../AppText';

function PantryDetails(props) {
  const {
    actions,
    registration,
    translation,
    locale,
    navigation,
    pantry: pantryReducer,
    goBack,
    navigateToProfile,
    organizationId,
  } = props;
  const { pantry, editCancelVisits, appointmentModal, visit: thisVisit } = pantryReducer;
  const { user } = registration;

  const [pushNotificationRegistration, setPushNotificationRegistration] = useState(false);
  const [reservationsAvailableNotif, setReservationsAvailableNotif] = useState(
    !!pantry.reservations_available_notifications
  );
  const [isEmbed, setIsEmbed] = useState(false);

  const stateProgramsEnabled = pantry?.state_programs ?? [];
  const [stateProgramRegistrationModal, setStateProgramRegistrationModal] = useState({
    current: undefined,
    remaining: stateProgramsEnabled,
  });

  const labels = {
    createReservation: translation.fragment_pantrydetail_button_createreservation,
    scheduleVisit: translation.fragment_pantrydetail_button_schedulevisit,
    amenities: translation.fragment_pantrydetail_textview_amenities,
    conditions: translation.fragment_pantrydetail_textview_conditions,
    notes: translation.fragment_pantrydetail_textview_notes,
    message: translation.fragment_pantrydetail_edittext_message,
    noDates: translation.dialog_visittime_textview_nodatesavailable,
    noShow: translation.visit_no_show,
    notServed: translation.visit_not_served,
    served: translation.visit_served,
    visitGroup: translation.visit_group,
    youAreGoing: translation.cell_pantry_short_visit_date,
    comeBy: translation.visit_come_by,
    receivedServiceOn: translation.received_service_on,
    missedServiceOn: translation.missed_reservation_on,
    serviceStartsAt: translation.service_starts_at,
    changeTime: translation.reschedule,
    cancelVisit: translation.cancel_visit,
    serviceNotifications: translation.reservations_available,
    serviceNotificationError: translation.service_notification_error,
    line: translation.app_servicekind_line,
    reservations: translation.app_servicekind_reservations,
    pre_registration: translation.pre_registration,
    reportIssue: translation.report_location_issue,
    distributionSchedule: translation.distribution_schedule,
    noDigitalOptions: translation.service_type_no_digital_options,
    reservationInline: translation.service_type_reservation_inline,
    allowsPreRegistration: translation.service_type_allows_pre_registration,
  };

  useEffect(() => {
    if (Platform.OS === 'web') {
      if (window.location !== window.parent.location) {
        setIsEmbed(true);
      }
    }
  }, []);

  useEffect(() => {
    if (pantry && pantry.id) {
      const evtParameters = {
        bookingAvailable: !!(pantry?.has_appointments && pantry?.new_options?.length), // this is the same logic used in the PantryDetails to determine whether to show the make appointment button
        organizationId: pantry?.id,
      };
      logAnalyticsEvent('pantry_details', evtParameters);
    }
  }, [pantry?.has_appointments, pantry?.new_options?.length]);

  useEffect(() => {
    if (reservationsAvailableNotif !== !!pantry.reservations_available_notifications) {
      setReservationsAvailableNotif(!!pantry.reservations_available_notifications);
    }
  }, [pantry.id]);

  const makeAppointment = () => {
    if (!isEmbed && user && user.id) {
      setPushNotificationRegistration(true);
      if (pantry.user_can_book) {
        actions.openMakeAppointment();
      } else {
        actions.openMustVerify();
      }
    } else {
      actions.openMustLogin();
    }
  };

  const confirmMakeAppointment = (pantryId, serviceDate, time) => {
    actions.makeAppointment(pantryId, organizationId, serviceDate, time);
    setReservationsAvailableNotif(true);
    resetStateProgramModals();
  };

  const confirmChangeAppointment = (visitId, serviceDate, time) => {
    if (serviceDate === null || time === null) {
      actions.closeMakeAppointment();
    } else {
      actions.changeAppointment(visitId, serviceDate, time, pantry.id);
      resetStateProgramModals();
    }
  };

  const closeMakeAppointment = () => {
    actions.closeMakeAppointment();
  };

  const showChangeModal = (visit) => {
    setPushNotificationRegistration(true);
    actions.openChangeAppointment(visit);
  };

  const showCancelModal = (visit) => {
    setPushNotificationRegistration(true);
    actions.openCancelAppointment(visit);
  };

  const showSelectEditCancelVisitModal = (upcoming, editCancel) => {
    setPushNotificationRegistration(true);
    actions.openSelectEditCancelVisit(upcoming, editCancel);
  };

  const openScreenTefapAttestation = () => {
    actions.openScreenTefapAttestation();
  };

  const closeScreenTefapAttestation = () => {
    const hasMissingInfo =
      !pantry.additional_info_confirmed_at && pantry.requested_fields && pantry.requested_fields.length > 0;
    actions.completeScreenTefapAttestation(hasMissingInfo);

    if (!hasMissingInfo) {
      // only skip to state programs if no AIQ, otherwise it'll open when AIQ is done
      openStateProgramModals();
    }
  };

  const openMissingInfoModal = () => {
    setPushNotificationRegistration(true);
    actions.openScreenMissingInfo();
  };

  const closeMissingInfoModal = () => {
    actions.closeScreenMissingInfo();

    // FIXME: only show state program modals if AIQ was started from post-reservation modal
    openStateProgramModals();
  };

  const closeReservationConfirmedModal = () => {
    // close the modal but don't open any subsequent ones
    // because reservation confirmed is already opening a next modal
    actions.closeReservationConfirmed();
  };

  const cancelReservationConfirmedModal = () => {
    // close knowing that there's not another follow-up modal coming
    closeReservationConfirmedModal();

    // open the follow up modal ourselves
    openStateProgramModals();
  };

  const openStateProgramModals = () => {
    /* TODO #3214: only use this once we've fixed the interactions with the complete buttons
    if (stateProgramRegistrationModal.remaining.length > 0) {
      setStateProgramRegistrationModal({
        // open the next state program
        current: stateProgramRegistrationModal.remaining[0],
        // but remove it from the queue so it doesn't open again
        remaining: stateProgramRegistrationModal.remaining.slice(1),
      });
    }
    */
  };

  const closeStateProgramModals = () => {
    if (stateProgramRegistrationModal.current) {
      setStateProgramRegistrationModal({
        current: undefined,
        remaining: stateProgramRegistrationModal.remaining,
      });
    }
  };

  const nextStateProgramModal = () => {
    // closes the previous modal and opens the next one but as one atomic state change
    const nextProgramModal = { ...stateProgramRegistrationModal };

    nextProgramModal.current = undefined;

    if (stateProgramRegistrationModal.remaining.length > 0) {
      nextProgramModal.current = stateProgramRegistrationModal.remaining[0];
      nextProgramModal.remaining = stateProgramRegistrationModal.remaining.slice(1);
    }

    setStateProgramRegistrationModal(nextProgramModal);
  };

  const resetStateProgramModals = () => {
    setStateProgramRegistrationModal({
      current: undefined,
      remaining: stateProgramsEnabled,
    });
  };

  const subscribeToServiceNotifications = async () => {
    setPushNotificationRegistration(true);
    setReservationsAvailableNotif(true);
    const success = await actions.subscribeServiceNotifications(pantry.pantry_id);
    if (!success) {
      setReservationsAvailableNotif(false);
      showAlert(labels.serviceNotificationError);
    }
  };

  const unsubscribeFromServiceNotifications = async () => {
    setPushNotificationRegistration(false);
    setReservationsAvailableNotif(false);
    const success = await actions.unsubscribeServiceNotifications(pantry.pantry_id);
    if (!success) {
      setReservationsAvailableNotif(true);
      showAlert(labels.serviceNotificationError);
    }
  };

  const closeMustVerify = () => {
    actions.closeMustVerify();
  };

  const closeMustLogin = () => {
    actions.closeMustLogin();
  };

  const goToLogin = () => {
    if (isEmbed) {
      return window.open('https://plntfl.app/l/app');
    }
    actions.closeMustLogin();
    goBack();
    navigation.navigate('Language');
  };

  let url = pantry.small_photo_url;
  if (url && url.substring(0, 4) !== 'http') {
    url = `https://s3.amazonaws.com/nyc-fac-dev/images/${url}`;
  }

  const combinedSchedule = [];

  let serviceToday = null;
  const timestamp = Math.floor(new Date() / 1000);
  const today = timeUtils.getServiceDateFromUnixTimestamp(timestamp);
  const now = timeUtils.getSeccondsSinceMidnightFromUnixTimestamp(timestamp);

  const [requestedFields, missingFields] = getAdditionalRequestedFields(user, pantry);

  if (pantry.service_hours && pantry.service_hours.length) {
    // lets make sure they are sorted ok
    const options = pantry.service_hours.sort((a, b) => {
      if (a.date > b.date) return 1;
      if (b.date > a.date) return -1;
      if (a.time > b.time) return 1;
      if (b.time > a.time) return -1;
      return 1;
    });

    // Get inline service type indicator based on service type
    const getServiceTypeIndicator = (serviceType) => {
      switch (serviceType) {
        case ServiceType.line:
          return `- ${labels.noDigitalOptions}`;
        case ServiceType.reservation:
          return `- ${labels.reservationInline}`;
        case ServiceType.pre_registration:
          return `- ${labels.allowsPreRegistration}`;
        default:
          return '';
      }
    };

    options.forEach((service, index) => {
      const text = timeUtils.getServiceRow(service, translation);
      const key = `Schedule-${service.date}-${index}`;
      const serviceTypeIndicator = getServiceTypeIndicator(service.service_type);

      const scheduleBlock = (
        <AppText key={key} style={[styles.infoText, styles.blackMedium]}>
          {text} {serviceTypeIndicator}
        </AppText>
      );

      combinedSchedule.push(scheduleBlock);

      if (service.date === today && service.time > now && !serviceToday) {
        serviceToday = service;
      }
    });
  }

  // Create single schedule array with Distribution Schedule header
  const fullSchedule =
    combinedSchedule.length > 0
      ? [
          [
            <AppText key="distribution-schedule" style={[styles.header2, styles.lineTitle]}>
              {labels.distributionSchedule}
            </AppText>,
            ...combinedSchedule,
          ],
        ]
      : [];

  const onShowLocation = () => {
    if (Platform.OS === 'web') {
      const link = `https://www.google.com/maps/search/?api=1&query=${pantry.latitude},${pantry.longitude}`;
      Linking.openURL(link);
    } else {
      try {
        showLocation({
          latitude: pantry.latitude,
          longitude: pantry.longitude,
          // sourceLatitude: -8.0870631,  // optionally specify starting location for directions
          // sourceLongitude: -34.8941619,  // not optional if sourceLatitude is specified
          title: pantry.name, // show a title for the pin (or search if not forced to use lat/long below)
          googleForceLatLon: true, // force GoogleMaps to use the lat/long for the query instead of the title
          dialogTitle: 'Open in maps app', // optional (default: 'Open in Maps')
          dialogMessage: 'Which map app to use?', // optional (default: 'What app would you like to use?')
          cancelText: 'Cancel, go back', // optional (default: 'Cancel')
          // appsWhiteList: ['google-maps'] // optionally you can set which apps to show (default: will show all supported apps installed on device)
          // appTitles: { 'google-maps': 'My custom Google Maps title' } // optionally you can override default app titles
          // app: 'uber'  // optionally specify specific app to use
        });
      } catch (error) {
        console.log(error);
      }
    }
  };

  const amenities = [];
  if (pantry.amenities && pantry.amenities.length) {
    pantry.amenities.forEach((amenity, index) => {
      amenities.push(
        <View style={styles.infoRow} key={`Amenity-${index}`}>
          <View style={styles.leftIconContainer}>
            <MaterialIcons name={'done'} size={24} color={Colors.primaryColor} />
          </View>
          <View>
            <AppText style={styles.infoText}>{amenity}</AppText>
          </View>
        </View>
      );
    });
  }

  const conditions = [];
  if (pantry.conditions && pantry.conditions.length) {
    pantry.conditions.forEach((condition, index) => {
      conditions.push(
        <View style={styles.infoRow} key={`Condition-${index}`}>
          <View style={styles.leftIconContainer}>
            <MaterialIcons name={'error'} size={24} color={Colors.errorBackground} />
          </View>
          <View>
            <AppText style={styles.infoText}>{condition}</AppText>
          </View>
        </View>
      );
    });
  }

  const defaultLocation = {
    latitude: 40.7073579,
    longitude: -74.0006498,
  };

  let verifyOrLoginModal = null;
  if (pantryReducer.showMustVerify) {
    verifyOrLoginModal = (
      <MustVerify onOk={navigateToProfile} onCancel={closeMustVerify} translation={translation} />
    );
  } else if (pantryReducer.showMustLogin) {
    verifyOrLoginModal = <MustLogin onOk={goToLogin} onCancel={closeMustLogin} translation={translation} />;
  }

  pantry.nextVisit = {};
  pantry.lastVisit = {};
  pantry.upcoming = [];
  pantry.editable = [];
  if (pantry.visits && pantry.visits.length) {
    // lets make sure they are sorted ok
    const visits = pantry.visits.sort((a, b) => {
      if (a.service_date > b.service_date) return -1;
      if (b.service_date > a.service_date) return 1;
      if (a.time > b.time) return -1;
      if (b.time > a.time) return 1;
      return 1;
    });
    visits.forEach((visit) => {
      visit.dateTime = timeUtils.dateAndTimeToDayjs(visit.service_date, visit.time);
      let isPast = false;
      if (visit.service_date < today) {
        isPast = true;
      } else {
        pantry.upcoming.push(visit); // consider all today and future upcoming visits
        if (!visit.served_at) pantry.editable.push(visit);
        if (visit.service_date === today && visit.served_at) {
          isPast = true;
        }
      }
      if (isPast) {
        if (!pantry.lastVisit.id || pantry.lastVisit.dateTime < visit.dateTime) {
          pantry.lastVisit = visit;
        }
      } else if (!pantry.nextVisit.id || pantry.nextVisit.dateTime > visit.dateTime) {
        pantry.nextVisit = visit;
      }
    });
  }

  let headerBackground = styles.headerBlue;
  let visitInfo = null;
  const visitInfoParams = {
    icon: null,
    label: null,
  };
  if (pantry.nextVisit && pantry.nextVisit.id) {
    headerBackground = styles.headerGreen;
    visitInfoParams.icon = 'directions-walk';
    visitInfoParams.label = '';
    pantry.upcoming.reverse().forEach((visit) => {
      const visitDate = timeUtils.getDateYear(visit.date * 1000, locale);
      const visitTime = timeUtils.timeSinceMidnightToString(visit.time);
      if (visit.service_type === ServiceType.pre_registration) {
        const visitUntilTime = timeUtils.timeSinceMidnightToString(visit.until_time);
        visitInfoParams.label += `${visitDate} ${visitTime} - ${visitUntilTime}\n`;
      } else {
        visitInfoParams.label += `${visitDate} ${visitTime}\n`;
      }
    });
  } else if (pantry.lastVisit && pantry.lastVisit.id) {
    const dateLabel = timeUtils.getDateYear(pantry.lastVisit.date * 1000, locale);
    visitInfoParams.icon = 'error';
    if (pantry.lastVisit.served_at) {
      visitInfoParams.label = `${labels.receivedServiceOn} ${dateLabel}`;
    } else {
      visitInfoParams.label = `${labels.missedServiceOn} ${dateLabel}`;
    }
  } else if (serviceToday) {
    const timeLabel = timeUtils.timeSinceMidnightToString(serviceToday.time);
    visitInfoParams.icon = 'schedule';
    visitInfoParams.label = `${labels.serviceStartsAt} ${timeLabel}`;
  }
  if (!isEmbed && visitInfoParams.label) {
    visitInfo = (
      <View style={[styles.infoRow, headerBackground, styles.visitRow]}>
        <View style={styles.visitIconContainer}>
          <MaterialIcons name={visitInfoParams.icon} size={24} color={Colors.white} />
        </View>
        <View>
          <AppText style={[styles.infoText, styles.whiteText]}>{visitInfoParams.label}</AppText>
        </View>
      </View>
    );
  }

  let changeCancel = null;
  if (pantry.editable && pantry.editable.length) {
    changeCancel = (
      <View style={styles.changeCancelContainer}>
        {pantry.disable_client_booking ? null : (
          <View style={styles.changeCancelButton}>
            <FilledButton
              onPress={() => {
                if (pantry.editable.length > 1) {
                  showSelectEditCancelVisitModal(pantry.editable, 'edit');
                } else {
                  showChangeModal(pantry.editable[0]);
                }
              }}
              label={labels.changeTime}
              color={Colors.lightBackground}
              full
              textColor={Colors.primaryColor}
              heigtht={56}
              radius={0}
            />
          </View>
        )}
        <View style={styles.changeCancelButton}>
          <FilledButton
            onPress={() => {
              if (pantry.editable.length > 1) {
                showSelectEditCancelVisitModal(pantry.editable, 'cancel');
              } else {
                showCancelModal(pantry.editable[0]);
              }
            }}
            label={labels.cancelVisit}
            color={Colors.brightRed}
            full
            textColor={Colors.white}
            heigtht={56}
            radius={0}
          />
        </View>
      </View>
    );
  }

  let pushComponent = null;
  if (user && user.id && pushNotificationRegistration) {
    pushComponent = <PushNotificationRegistration user={user} actions={actions} translation={translation} />;
  }

  const showMakeAppointmentButton = !changeCancel && pantry.has_appointments && pantry.new_options.length;
  const isPreRegistration = pantry?.service_hours?.some((serviceHour) => serviceHour.service_type === 3);
  const serviceNotificationSwitch = useMemo(
    () => (
      <MaterialSwitch
        onActivate={subscribeToServiceNotifications}
        onDeactivate={unsubscribeFromServiceNotifications}
        active={reservationsAvailableNotif}
      />
    ),
    [pantry.id, reservationsAvailableNotif]
  );

  const programsLabels = [];
  if (pantry.programs && pantry.programs.length) {
    pantry.programs.forEach((program) => {
      const programLabel = program.toLowerCase().replace(/ /g, '_');
      programsLabels.push(translation[`program_type_${programLabel}`]);
    });
  }
  const programsLabel = programsLabels.sort().join(', ');

  let pantryWebsiteUrl = null;
  if (
    pantry.website &&
    typeof pantry.website === 'string' &&
    pantry.website !== '' &&
    pantry.website.indexOf('@') == -1 &&
    pantry.website.trim().indexOf(' ') == -1 &&
    pantry.website.indexOf('.') !== -1
  ) {
    if (pantry.website.indexOf('https://') !== -1) {
      pantryWebsiteUrl = pantry.website.trim().toLowerCase();
    } else if (pantry.website.indexOf('http://') !== -1) {
      pantryWebsiteUrl = pantry.website.trim().toLowerCase();
    } else {
      pantryWebsiteUrl = `http://${pantry.website.toLowerCase()}`;
    }
  }

  const icon = getMapMarkerIcon(pantry);

  return (
    <View style={styles.view}>
      <ScrollView keyboardShouldPersistTaps="handled">
        {Layout.usePantryTabs ? (
          <View style={styles.mapContainer}>
            {!pantry.id ? (
              <PantrySpinner translation={translation} showHeader={false} />
            ) : (
              <MapView
                style={styles.mapStyle}
                initialRegion={{
                  latitude: pantry.latitude || defaultLocation.latitude,
                  longitude: pantry.longitude || defaultLocation.longitude,
                  latitudeDelta: 0.05,
                  longitudeDelta: 0.15,
                }}
                onRegionChangeComplete={() => {}}
              >
                <PantryDetailsMarker pantry={pantry} defaultLocation={defaultLocation} icon={icon} />
              </MapView>
            )}
          </View>
        ) : null}
        <View style={[styles.titleContainer, headerBackground]}>
          {!Layout.usePantryTabs ? (
            <View style={styles.goBackContainer}>
              <TouchableOpacity style={styles.iconContainer} onPress={goBack}>
                <MaterialIcons name={'arrow-back'} size={24} color={Colors.white} />
              </TouchableOpacity>
            </View>
          ) : null}
          <View style={styles.imageContainer}>
            <Image source={{ uri: url }} style={styles.pantryImage} />
          </View>
          <View style={styles.titleTextContainer}>
            <AppText style={styles.titleText}>{pantry.name}</AppText>
            {programsLabel ? <AppText style={styles.titleSubText}>{programsLabel}</AppText> : null}
            {typeof pantry.distance === 'number' ? (
              <PantryDistance distance={pantry.distance} style={styles.pantryDistance} />
            ) : null}
            {Layout.isSmall ? null : visitInfo}
          </View>
          {Layout.isSmall ? visitInfo : null}
        </View>

        {/* Show any state programs opted in, let program logic decide how to show if there's not a visit yet */}
        {stateProgramsEnabled.length > 0 ? (
          <View testID="PantryDetailsStateProgramWrapper">
            {stateProgramsEnabled.map((programId, index) => (
              <StateProgramsErrorBoundary
                key={index}
                fallback={null}
                errorReporter={logError}
                programId={programId}
                visitId={pantry?.nextVisit?.id}
                providerId={pantry.id}
              >
                <StateProgramContextProvider
                  programId={programId}
                  programServicesFactory={StateProgramFacade.buildForProgram}
                >
                  <PantryDetailsProgramPrompt
                    visitId={pantry?.nextVisit?.id}
                    providerId={pantry.id}
                    providerState={pantry.state}
                    registration={registration}
                    closePromptModal={
                      stateProgramRegistrationModal.current === programId
                        ? closeStateProgramModals
                        : undefined
                    }
                    nextModal={
                      stateProgramRegistrationModal.current === programId ? nextStateProgramModal : undefined
                    }
                  />
                </StateProgramContextProvider>
              </StateProgramsErrorBoundary>
            ))}
          </View>
        ) : null}

        {/* Only show legacy program prompts if we're not embedded and if we already have a visit */}
        {!isEmbed && pantry.nextVisit && pantry.nextVisit.id && pantry.use_tefap ? (
          <View style={styles.tefapContainer}>
            <TefapStatus
              actions={actions}
              registration={registration}
              pantry={pantry}
              organizationId={organizationId}
            />
          </View>
        ) : null}

        {!isEmbed &&
        pantry?.nextVisit?.id &&
        pantry.requested_fields &&
        pantry.requested_fields.length > 0 &&
        !pantry.additional_info_confirmed_at ? (
          <AdditionalInfoRequired showModal={openMissingInfoModal} />
        ) : null}

        <View style={styles.infoContainer}>
          <View style={styles.infoRow}>
            {pantry.user_visit_summary && (
              <UserVisitSummaryWithIcon
                message={pantry.user_visit_summary}
                userCanVisit={pantry.user_can_visit}
              />
            )}
          </View>

          {!isEmbed &&
            fullSchedule.length > 0 &&
            fullSchedule.map((schedule, index) => (
              <ExpandableServiceSchedule
                key={`service-schedule-${index}`}
                index={index}
                schedule={schedule}
              />
            ))}

          <View style={styles.infoRow}>
            <View style={styles.leftIconContainer}>
              <MaterialIcons name={'location-on'} size={24} color={Colors.primaryColor} />
            </View>
            <View>
              <AppText onPress={onShowLocation} style={[styles.infoText, styles.link]}>
                {pantry.address}
              </AppText>
            </View>
          </View>

          <View style={styles.infoRow}>
            <View style={styles.leftIconContainer}>
              <MaterialIcons name={'phone'} size={24} color={Colors.primaryColor} />
            </View>
            <View>
              <AppText
                onPress={() => {
                  Linking.openURL(`tel:${pantry.phone}`);
                }}
                style={[styles.infoText, styles.link]}
              >
                {formatPhoneNumber(pantry.phone)}
              </AppText>
            </View>
          </View>

          {pantryWebsiteUrl ? (
            <View style={styles.infoRow}>
              <View style={styles.leftIconContainer}>
                <MaterialIcons name={'public'} size={24} color={Colors.primaryColor} />
              </View>
              <View>
                <AppText
                  style={[styles.infoText, styles.link]}
                  onPress={() => Linking.openURL(pantryWebsiteUrl)}
                >
                  {pantry.website.toLowerCase()}
                </AppText>
              </View>
            </View>
          ) : null}

          {pantry.email ? (
            <View style={styles.infoRow}>
              <View style={styles.leftIconContainer}>
                <MaterialIcons name={'email'} size={24} color={Colors.primaryColor} />
              </View>
              <View>
                <AppText
                  style={[styles.infoText, styles.link]}
                  onPress={() => Linking.openURL(`mailto:${pantry.email}`)}
                >
                  {pantry.email}
                </AppText>
              </View>
            </View>
          ) : null}
        </View>

        <View style={styles.additionalContainer}>
          {amenities.length ? (
            <View>
              <AppText style={[styles.header2, styles.listTitle]}>{labels.amenities}</AppText>
              {amenities}
            </View>
          ) : null}
          {conditions.length ? (
            <View>
              <AppText style={[styles.header2, styles.listTitle]}>{labels.conditions}</AppText>
              {conditions}
            </View>
          ) : null}
          {pantry.notes && pantry.notes !== '' ? (
            <View>
              <AppText style={styles.header2}>{labels.notes}</AppText>
              <AppText style={styles.notesText}>{pantry.notes}</AppText>
            </View>
          ) : null}
          {!isPlentifulProvider(pantry) ? (
            <View style={styles.infoRow}>
              <View style={styles.leftIconContainer}>
                <MaterialIcons name={'flag'} size={24} color={Colors.primaryColor} />
              </View>
              <View>
                <AppText
                  style={[styles.infoText, styles.link]}
                  onPress={() => {
                    const baseUrl = `${EnvVars.reportIssueUrl}${organizationId}`;
                    const feedbackSubmitUrl =
                      user && user.email ? `${baseUrl}&userEmail=${encodeURIComponent(user.email)}` : baseUrl;
                    Linking.openURL(feedbackSubmitUrl);
                  }}
                >
                  {labels.reportIssue}
                </AppText>
              </View>
            </View>
          ) : null}
          {isPlentifulProvider(pantry) && user && user.id && !isEmbed ? (
            // Switch seems to not honour the `disabled` prop, so unfortunately it is neecessary to restory to hiding the
            // control completely for non-Plentiful-Provider locations
            <View style={styles.notifyContainer}>
              <AppText style={styles.notifyText}>{labels.serviceNotifications}</AppText>
              {serviceNotificationSwitch}
            </View>
          ) : null}
        </View>
      </ScrollView>
      {Layout.usePantryTabs ? (
        <View style={styles.hangingMenu}>
          <LinearGradient colors={['rgba(0,0,0,0.8)', 'transparent']} style={styles.mapOverlay} />
          <View style={styles.topMenu}>
            <TouchableOpacity style={styles.iconContainer} onPress={goBack}>
              <MaterialIcons name={'arrow-back'} size={30} color={Colors.white} />
            </TouchableOpacity>
          </View>
        </View>
      ) : null}
      {showMakeAppointmentButton ? (
        <TouchableOpacity style={styles.createNextAppointment} onPress={makeAppointment}>
          <AppText style={styles.buttonText}>
            {isPreRegistration ? labels.scheduleVisit : labels.createReservation}
          </AppText>
        </TouchableOpacity>
      ) : null}
      {changeCancel}
      {/* TODO #3143: Refactor the post-registration modals to have their flow controlled from a central place */}
      <View style={styles.modalContainer}>
        {appointmentModal == 'showSelect' ? (
          <MakeAppointment
            options={pantry.new_options}
            pantry={pantry}
            actions={actions}
            makeAppointment={confirmMakeAppointment}
            changeAppointment={confirmChangeAppointment}
            closeMakeAppointment={closeMakeAppointment}
            translation={translation}
            locale={locale}
            user={user}
          />
        ) : null}
        {appointmentModal == 'showCancel' ? (
          <ConfirmCancel
            actions={actions}
            translation={translation}
            visit={pantryReducer.visit}
            pantry={pantry}
            organizationId={organizationId}
          />
        ) : null}
        {appointmentModal == 'showChange' ? (
          <MakeAppointment
            options={pantry.edit_options || []}
            pantry={pantry}
            visit={thisVisit}
            makeAppointment={confirmMakeAppointment}
            changeAppointment={confirmChangeAppointment}
            closeMakeAppointment={closeMakeAppointment}
            translation={translation}
            locale={locale}
          />
        ) : null}
        {appointmentModal == 'showSelectEditCancel' ? (
          <SelectEditCancelVisit
            actions={actions}
            visits={editCancelVisits.visits}
            translation={translation}
            locale={locale}
            editOrCancel={editCancelVisits.editOrCancel}
          />
        ) : null}
        {appointmentModal == 'showReservationConfirmed' ? (
          <ReservationConfirmedModal
            translation={translation}
            visit={thisVisit}
            closeModal={closeReservationConfirmedModal}
            cancelModal={cancelReservationConfirmedModal}
            showTefapAttestationModal={openScreenTefapAttestation}
            showMissingInfoModal={openMissingInfoModal}
            locale={locale}
            user={user}
            registration={registration}
            actions={actions}
            hasMissingInfo={
              !pantry.additional_info_confirmed_at &&
              pantry.requested_fields &&
              pantry.requested_fields.length > 0
            }
            pantry={pantry}
            organizationId={organizationId}
          />
        ) : null}
        {appointmentModal == 'showScreenTefapAttestation' ? (
          <TefapAttestation
            isVisible={true}
            onCancel={closeScreenTefapAttestation}
            translation={translation}
            registration={registration}
            actions={actions}
            visit={thisVisit}
            pantry={pantry}
            organizationId={organizationId}
          />
        ) : null}
        {appointmentModal == 'showScreenMissingInfo' ? (
          <MissingInfoModal
            translation={translation}
            actions={actions}
            locale={locale}
            profile={registration.user}
            missingFields={missingFields}
            requestedFields={requestedFields}
            onBackdropPress={closeMissingInfoModal}
            pantry={pantry}
          />
        ) : null}
        {pushComponent}
        {verifyOrLoginModal}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  view: {
    backgroundColor: Colors.white,
    flex: 1,
    position: 'relative',
  },
  mapContainer: {
    height: 200,
    position: 'relative',
  },
  hangingMenu: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
  },
  iconContainer: {
    width: 50,
    height: 50,
    paddingLeft: Layout.isSmall ? 0 : 16,
    paddingTop: Layout.usePantryTabs ? 0 : 16,
  },
  topMenu: {
    paddingLeft: 16,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    paddingTop: 40,
    height: 100,
  },
  mapStyle: {
    width: Layout.usePantryTabs ? Layout.fullWidth : 0.5 * Layout.fullWidth,
    height: 200,
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  mapOverlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    height: 80,
  },
  titleContainer: {
    position: 'relative',
    display: 'flex',
    flexDirection: Layout.isSmall ? 'column' : 'row',
    minHeight: Layout.isSmall ? 'auto' : 168,
  },
  headerBlue: {
    backgroundColor: Colors.primaryColor,
  },
  headerGreen: {
    backgroundColor: Colors.primaryColor,
  },
  imageContainer: {
    position: 'relative',
    height: 0,
  },
  pantryImage: {
    position: Layout.isSmall ? 'absolute' : 'relative',
    left: 16,
    top: Layout.isSmall ? -16 : Layout.usePantryTabs ? 16 : 40,
    width: 96,
    height: 96,
    borderRadius: 4,
    backgroundColor: Colors.white,
  },
  pantryDistance: {
    color: Colors.white,
    marginTop: 4,
  },
  titleTextContainer: {
    marginLeft: Layout.isSmall ? 128 : 32,
    marginRight: 16,
    paddingTop: Layout.usePantryTabs ? 16 : 40,
    minHeight: Layout.isSmall ? 96 : 168,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
  },
  titleText: {
    fontSize: 16,
    color: Colors.white,
    flexWrap: 'wrap',
  },
  titleSubText: {
    fontSize: 14,
    color: Colors.white,
  },
  tefapContainer: {
    borderBottomColor: Colors.grayBorder,
    borderBottomWidth: 1,
  },
  infoContainer: {
    flexDirection: 'column',
    paddingTop: 24,
  },
  infoRow: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: 24,
    paddingLeft: 16,
    paddingRight: 16,
  },
  infoText: {
    color: Colors.veryDarkGray,
    fontSize: 14,
    lineHeight: 24,
    flexWrap: 'wrap',
    width: Layout.usePantryTabs ? Layout.fullWidth - 73 : 0.5 * Layout.fullWidth - 73,
  },
  blackMedium: {
    color: Colors.veryDarkGray,
    fontWeight: '500',
  },
  whiteText: {
    color: Colors.white,
  },
  link: {
    color: Colors.primaryColor,
  },
  moreLink: {
    color: Colors.primaryColor,
    textTransform: 'uppercase',
    fontFamily: 'roboto-regular',
    fontWeight: '700',
    textDecorationLine: 'underline',
    marginTop: 16,
  },
  leftIconContainer: {
    marginRight: 16,
  },
  visitIconContainer: {
    marginRight: 12,
    paddingLeft: Layout.isSmall ? 16 : 0,
  },
  visitRow: {
    marginTop: 12,
  },
  createNextAppointment: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 56,
    backgroundColor: Colors.primaryColor,
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  noNextAppointment: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 56,
    backgroundColor: Colors.grayText,
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonText: {
    textTransform: 'uppercase',
    color: Colors.white,
    fontWeight: '700',
  },
  additionalContainer: {
    paddingTop: 16,
    paddingBottom: 64,
    borderTopColor: Colors.grayBorder,
    borderTopWidth: 1,
    backgroundColor: Colors.white,
  },
  header2: {
    color: Colors.primaryColor,
    fontWeight: 'bold',
    fontSize: 16,
    marginBottom: 8,
    paddingLeft: 16,
  },
  scheduleTitle: {
    paddingLeft: 0,
    textTransform: 'capitalize',
  },
  lineTitle: {
    paddingLeft: 0,
    marginTop: 16,
    textTransform: 'capitalize',
  },
  listTitle: {
    paddingBottom: 16,
  },
  notesText: {
    lineHeight: 24,
    color: Colors.veryDarkGray,
    marginLeft: 16,
    marginRight: 16,
  },
  messageContainer: {
    marginBottom: 200,
    backgroundColor: Colors.white,
  },
  changeCancelContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: Colors.white,
    flex: 1,
    flexDirection: 'row',
  },
  changeCancelButton: {
    width: '50%',
  },
  changeTime: {
    textTransform: 'uppercase',
    fontWeight: '600',
    lineHeight: 56,
  },
  cancelVisit: {
    textTransform: 'uppercase',
    fontWeight: '600',
    lineHeight: 56,
    color: Colors.brightRed,
  },
  notifyContainer: {
    flexDirection: 'row',
    marginHorizontal: 16,
    marginBottom: 16,
    marginTop: 32,
  },
  notifyText: {
    fontSize: 16,
    lineHeight: 28,
    marginRight: 20,
    maxWidth: Layout.fullWidth - 100,
  },
});

PantryDetails.propTypes = {
  actions: PropTypes.objectOf(PropTypes.any).isRequired,
  goBack: PropTypes.func.isRequired,
  locale: PropTypes.string.isRequired,
  navigateToProfile: PropTypes.func.isRequired,
  organizationId: PropTypes.number.isRequired,
  navigation: PropTypes.objectOf(PropTypes.any).isRequired,
  pantry: PropTypes.objectOf(PropTypes.any).isRequired,
  registration: PropTypes.objectOf(PropTypes.any).isRequired,
  route: PropTypes.objectOf(PropTypes.any),
  translation: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default PantryDetails;

// Object {
//   "address": "2101 E 16th Street  2Fl",
//   "amenities": Array [
//     "Client choice",
//     "Distributes food based on household size",
//     "Speaks Chinese",
//   ],
//   "conditions": Array [
//     "1 Visit per Per Week",
//   ],
//   "email": "healthessentialassociation@hotmail.com",
//   "frequency_limitations": "per_week",
//   "frequency_limitations_count": 1,
//   "has_appointment": false,
//   "has_appointments": false,
//   "has_line_open": false,
//   "id": 220,
//   "latitude": 40.599876,
//   "longitude": -73.990746,
//   "name": "Health Essential Association",
//   "notes": null,
//   "phone": 6465156898,
//   "reservations_available_notifications": false,
//   "service_hours": Array [
//     Object {
//       "date": "2020-06-19",
//       "max_people": 0,
//       "time": 36000,
//       "time_interval": 15,
//       "type": "first_come_first_serve",
//       "until": 43200,
//     },
//     Object {
//       "date": "2020-06-26",
//       "max_people": 0,
//       "time": 36000,
//       "time_interval": 15,
//       "type": "first_come_first_serve",
//       "until": 43200,
//     },
//   ],
//   "small_photo_url": "203b4787-b30b-4416-91c9-8d3ef584dee4.jpg",
//   "subscribed": false,
//   "timezone": "America/New_York",
//   "type": null,
//   "website": null,
// }
